import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader, CheckCircle, XCircle, Lock } from "lucide-react";
import { API, getErrorMessage } from "../lib/api";

export default function ResetPasswordView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReset = async () => {
    if (password !== confirm) { setError("Passordene stemmer ikke overens"); return; }
    if (password.length < 8) { setError("Passord ma vaere minst 8 tegn"); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (e: any) {
      setError(getErrorMessage(e, "Kunne ikke tilbakestille passord. Lenken kan ha utlopt."));
    } finally { setLoading(false); }
  };

  if (!token) return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md text-center">
        <XCircle className="w-12 h-12 text-danger mx-auto mb-4" />
        <h2 className="text-[18px] font-bold text-ink-900 mb-2">Ugyldig lenke</h2>
        <p className="text-[13px] text-ink-500 mb-4">Denne lenken er ugyldig eller utlopt. Be om en ny tilbakestilling.</p>
        <button onClick={() => navigate("/")} className="px-6 py-2.5 rounded-xl text-[13px] font-semibold bg-brand-600 text-white hover:bg-brand-500 transition-all">
          Ga til innlogging
        </button>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md text-center">
        <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
        <h2 className="text-[18px] font-bold text-ink-900 mb-2">Passord tilbakestilt</h2>
        <p className="text-[13px] text-ink-500 mb-4">Du kan na logge inn med ditt nye passord.</p>
        <button onClick={() => navigate("/")} className="px-6 py-2.5 rounded-xl text-[13px] font-semibold bg-brand-600 text-white hover:bg-brand-500 transition-all">
          Ga til innlogging
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-surface-200 shadow-soft p-8 max-w-md w-full">
        <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Lock className="w-6 h-6 text-brand-600" />
        </div>
        <h2 className="text-[18px] font-bold text-ink-900 text-center mb-1">Tilbakestill passord</h2>
        <p className="text-[13px] text-ink-500 text-center mb-6">Velg et nytt passord for kontoen din.</p>

        {error && <div className="bg-danger/10 border border-danger/20 rounded-xl p-3 mb-4 text-[12px] text-danger">{error}</div>}

        <div className="space-y-3">
          <input value={password} onChange={e => setPassword(e.target.value)} type="password"
            placeholder="Nytt passord (minst 8 tegn, 1 stor bokstav, 1 tall)"
            className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] focus:bg-white transition-all" />
          <input value={confirm} onChange={e => setConfirm(e.target.value)} type="password"
            placeholder="Bekreft nytt passord"
            className="w-full bg-surface-50 border border-surface-200 rounded-xl px-4 py-3 text-[13px] focus:bg-white transition-all" />
          <button onClick={handleReset} disabled={loading || !password || !confirm}
            className="w-full py-3 rounded-xl text-[14px] font-semibold bg-brand-600 text-white hover:bg-brand-500 disabled:bg-surface-200 disabled:text-ink-400 flex items-center justify-center gap-2 transition-all">
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : "Tilbakestill passord"}
          </button>
        </div>
      </div>
    </div>
  );
}
